import PropTypes from '~/lib/propTypes'
import styled from '@emotion/styled'
import { space, device } from '@ticketswap/solar'
import useLanguage from '~/hooks/useLanguage'
import { track } from '~/utils/analytics'
import { APP_DOWNLOAD_CLICKED } from '~/lib/tracking-events'
import { useTranslation } from '~/components/TranslationContext'
import useStats from '~/hooks/useStats'
import { useDeviceInfo } from '~/components/DeviceInfoContext'
import getConfig from 'next/config'

const { publicRuntimeConfig } = getConfig()

const List = styled.ul`
  display: flex;
  flex-direction: column;

  @media ${device.tablet} {
    flex-direction: row;
  }

  li:not(:first-of-type) {
    margin-block-start: ${space[8]};

    @media ${device.tablet} {
      margin-block-start: 0;
      margin-inline-start: ${space[16]};
    }
  }

  img {
    vertical-align: middle;
    max-width: 100%;
    max-height: ${space[48]};
  }
`

const BadgeItem = styled.li`
  display: flex;
  flex-direction: column;
`

const Stars = styled.div`
  margin-block-start: ${space[8]};
`

export const AppStoreBadge = () => {
  const language = useLanguage()

  const getImageUrl = () => {
    const cdnUrl = publicRuntimeConfig.NEXT_PUBLIC_CDN_URL

    switch (language) {
      case 'de':
        return `${cdnUrl}/static/images/appStore/app-store-de.svg`
      case 'es':
        return `${cdnUrl}/static/images/appStore/app-store-es.svg`
      case 'fr':
        return `${cdnUrl}/static/images/appStore/app-store-fr.svg`
      case 'hu':
        return `${cdnUrl}/static/images/appStore/app-store-hu.svg`
      case 'nl':
        return `${cdnUrl}/static/images/appStore/app-store-nl.svg`
      case 'pt':
        return `${cdnUrl}/static/images/appStore/app-store-pt.svg`
      default:
        return `${cdnUrl}/static/images/appStore/app-store-en.svg`
    }
  }

  return <img src={getImageUrl()} loading="lazy" alt="App Store" width={120} height={40} />
}

export const GooglePlayBadge = () => {
  const language = useLanguage()

  const getImageUrl = () => {
    switch (language) {
      case 'de':
        return '/static/images/appStore/google-play-de.svg'
      case 'es':
        return '/static/images/appStore/google-play-es.svg'
      case 'fr':
        return '/static/images/appStore/google-play-fr.svg'
      case 'hu':
        return '/static/images/appStore/google-play-hu.svg'
      case 'nl':
        return '/static/images/appStore/google-play-nl.svg'
      case 'pt':
        return '/static/images/appStore/google-play-pt.svg'
      default:
        return '/static/images/appStore/google-play-en.svg'
    }
  }

  return <img src={getImageUrl()} loading="lazy" alt="Google Play" width={135} height={40} />
}

export const AppStoreLink = ({ children, trackingContext = 'General' }) => {
  return (
    <a
      href={`https://apps.apple.com/app/apple-store/id932337449?pt=76205801&ct=${encodeURIComponent(
        trackingContext.toLowerCase()
      )}&mt=8`}
      onClick={() =>
        track(APP_DOWNLOAD_CLICKED, {
          store: 'App Store',
          trigger: trackingContext,
        })
      }
    >
      {children}
    </a>
  )
}

export const GooglePlayLink = ({ children, trackingContext = 'General' }) => {
  return (
    <a
      href={`https://play.google.com/store/apps/details?id=com.ticketswap.ticketswap&referrer=utm_source%internal%26utm_medium%3Dapp_button%26utm_term%3D${encodeURIComponent(
        trackingContext.toLowerCase()
      )}`}
      onClick={() =>
        track(APP_DOWNLOAD_CLICKED, {
          store: 'Google Play',
          trigger: trackingContext,
        })
      }
    >
      {children}
    </a>
  )
}

const AppStoreBadges = ({ trackingContext = 'General', showRatings = false, className }) => {
  const { t } = useTranslation()
  const deviceInfo = useDeviceInfo()
  const { isDesktop, isMobile, isIOS, isApp, isAndroid } = deviceInfo
  const showAppStoreLink = isDesktop || ((isMobile || isApp) && isIOS)
  const showGooglePlayLink = isDesktop || ((isMobile || isApp) && isAndroid)

  const { appStoreReviewsLabel, playStoreReviewsLabel, appStoreStars, playStoreStars } = useStats({
    t,
    starSize: 20,
  })

  return (
    <List className={className} role="listbox">
      {showAppStoreLink && (
        <BadgeItem>
          <AppStoreLink trackingContext={trackingContext}>
            <AppStoreBadge />
          </AppStoreLink>
          {showRatings && (
            <>
              <Stars>{appStoreStars}</Stars>
              {appStoreReviewsLabel}
            </>
          )}
        </BadgeItem>
      )}
      {showGooglePlayLink && (
        <BadgeItem>
          <GooglePlayLink trackingContext={trackingContext}>
            <GooglePlayBadge />
          </GooglePlayLink>
          {showRatings && (
            <>
              <Stars>{playStoreStars}</Stars>
              {playStoreReviewsLabel}
            </>
          )}
        </BadgeItem>
      )}
    </List>
  )
}

AppStoreBadges.propTypes = {
  trackingContext: PropTypes.string,
  showRatings: PropTypes.bool,
  className: PropTypes.string,
}

export default AppStoreBadges
